import { Body, Box, Button, Drawer, Heading, Image } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks/useHomeownerAppContext';
import { createAiOriginalImage } from 'src/modules/homeownerApp/services/createAiOriginalImage';
import { LinkExternal } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { External } from 'src/shared/route';

import { SpaceTypeSelector } from './SpaceTypeSelector';

const VERTICAL_DRAWER_PADDING_PIXELS = 40;

const UploadImage = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [spaceType, setSpaceType] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const { imageFileToUpload, setImageFileToUpload, user } =
    useHomeownerAppContext();

  useEffect(() => {
    const url = imageFileToUpload ? URL.createObjectURL(imageFileToUpload) : '';
    setImageUrl(url);
    return url ? () => URL.revokeObjectURL(url) : () => null;
  }, [imageFileToUpload]);

  const handleClose = () => {
    setIsUploading(false);
    setImageFileToUpload();
    setSpaceType('');
  };

  if (!imageFileToUpload) return null;

  const handleGenerateImages = async () => {
    setIsUploading(true);
    await createAiOriginalImage(imageFileToUpload, {
      spaceType,
      sessionId: '',
      email: user?.email,
    });
    setImageFileToUpload();
    setIsUploading(false);
  };

  return (
    <Drawer
      autoFocus={false}
      isOpen={!!imageUrl}
      onClose={handleClose}
      placement="bottom"
    >
      <Box
        alignItems="flex-start"
        backgroundColor="white"
        flexDirection={{ base: 'column', tablet: 'row-reverse' }}
        height={{
          base: `calc(100vh - ${VERTICAL_DRAWER_PADDING_PIXELS}px)`,
          tablet: 800,
        }}
        overflow="scroll"
      >
        <Box
          alignItems="flex-end"
          flexDirection="row"
          flexShrink={1}
          height={{ base: '200px', tablet: '100%' }}
          justifyContent={{ base: 'flex-start', tablet: 'center' }}
          paddingBottom={{ base: 400, tablet: 600 }}
          paddingTop={{ base: undefined, tablet: 600 }}
          width={{ base: '100%', tablet: '40%' }}
        >
          <Image
            alt="uploaded image"
            display="block"
            height="100%"
            objectFit="contain"
            src={imageUrl}
            width={{ base: undefined, tablet: '100%' }}
          />
        </Box>
        <Box flexDirection="column" maxWidth={{ base: '100%', tablet: '60%' }}>
          <Heading fontWeight={600} size={{ base: 500, tablet: 600 }}>
            {t('uploadImage.addLabel')}
          </Heading>
          <SpaceTypeSelector
            setSpaceType={setSpaceType}
            spaceType={spaceType}
          />
          <Body color="textLight" size={{ base: 300, tablet: 500 }}>
            <Trans
              components={{
                TermsOfServiceLink: (
                  <LinkExternal
                    color="inherit"
                    href={External.TERMS_OF_SERVICE.path}
                  />
                ),
                PrivacyPolicyLink: (
                  <LinkExternal
                    color="inherit"
                    href={External.PRIVACY_POLICY.path}
                  />
                ),
              }}
              i18nKey="uploadImage.legalText"
              t={t}
            />
          </Body>
          <Button
            isDisabled={!spaceType}
            isLoading={isUploading}
            maxWidth="fit-content"
            onClick={handleGenerateImages}
          >
            {t('uploadImage.generateImages')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export { UploadImage };
